import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Empty, Skeleton, Switch } from 'antd';
import { payMethodExhibitQuery } from '../../queries';
import { useUpdatePayMethodExhibitsMutation } from '../../hooks/usePgProviderRouteMutations';
import { useSettingsDraft } from '../../hooks/useSettingsDraft';
import { SettingsPanelProps } from '../../types';
import { SettingsSection } from '../../common/SettingsSection';
import { SettingsSaveBar } from '../../common/SettingsSaveBar';
import { SettingsReviewModal } from '../../common/SettingsReviewModal';
import { SortableSettingRow } from '../../common/SortableSettingRow';
import {
  getPayMethodChanges,
  moveItem,
  toComparablePayMethods,
  toEditablePayMethods,
  toPayMethodPayload,
} from '../../utils/settingsChanges';
import { extractErrorMessage } from '../../../../lib/utils/checkErrorResponse';
import { PayMethodBenefitEditor } from './PayMethodBenefitEditor';
import { PayMethodWorkspaceStyle } from './styles';

export const PayMethodExhibitPanel = (props: SettingsPanelProps): React.JSX.Element => {
  const { canUpdate, onDirtyChange } = props;
  const query = useQuery(payMethodExhibitQuery.list());
  const mutation = useUpdatePayMethodExhibitsMutation();
  // 안내문 줄의 편집 키는 화면 전용이라 미저장 판정에서 뺀다
  const draft = useSettingsDraft(
    query.data && toEditablePayMethods(query.data.payMethods),
    toComparablePayMethods,
  );
  const { message } = App.useApp();
  const [selectedId, setSelectedId] = useState<number>();
  const [isReviewOpen, setReviewOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => onDirtyChange(draft.isDirty), [draft.isDirty, onDirtyChange]);

  if (query.isError)
    return (
      <Alert
        type="error"
        showIcon
        message="결제수단 설정을 불러오지 못했습니다."
        action={<Button onClick={() => query.refetch()}>다시 시도</Button>}
      />
    );
  if (!draft.value || !draft.baseline) return <Skeleton active paragraph={{ rows: 8 }} />;
  const list = draft.value;
  const selected = list.find((item) => item.id === selectedId) ?? list[0];
  const disabled = !canUpdate || mutation.isLoading;
  const changes = getPayMethodChanges(draft.baseline, list);
  const hasBlankLine = list.some((item) => item.eventDetails.some((detail) => !detail.content.trim()));

  /**
   * 🔴 저장 직전에 한 번 더 읽어 다른 사람의 변경을 확인한다.
   *
   * 이 화면은 열어 둔 뒤 자동으로 다시 조회하는 경로가 없다 — 전역 `refetchOnWindowFocus` 가
   * 꺼져 있고 탭은 `forceRender` 라 재마운트되지 않는다. 그래서 확인 없이 저장하면
   * 그 사이 다른 관리자가 바꾼 내용을 모른 채 덮어쓴다(서버 잠금은 동시에 들어온 저장을
   * 줄 세울 뿐, 오래된 기준값으로 온 요청은 막지 못한다).
   */
  const handleReview = async (): Promise<void> => {
    setSaveError(null);
    const { data } = await query.refetch();

    if (draft.isOutdated(data && toEditablePayMethods(data.payMethods))) {
      message.warning('다른 관리자가 설정을 바꿨습니다. 변경 취소 후 최신 설정으로 다시 편집해 주세요.');

      return;
    }
    setReviewOpen(true);
  };
  const handleSave = async (): Promise<void> => {
    if (!canUpdate || mutation.isLoading || draft.hasRemoteChanges) return;
    setSaveError(null);
    try {
      await mutation.mutateAsync({ payMethods: toPayMethodPayload(list) });
      draft.reset();
      setReviewOpen(false);
      message.success('결제수단·혜택 설정을 저장했습니다.');
    } catch (error) {
      setSaveError(error instanceof Error ? extractErrorMessage(error) : '저장하지 못했습니다. 다시 시도해 주세요.');
    }
  };

  return (
    <>
      <div className="settings-panel-header">
        <div>
          <h2>고객에게 보이는 결제수단과 혜택</h2>
          <p>목록에서 순서와 노출을 정하고, 결제수단을 선택해 문구를 편집하세요.</p>
        </div>
      </div>
      {draft.hasRemoteChanges && (
        <Alert
          className="settings-inline-alert"
          type="warning"
          showIcon
          message="다른 변경 사항이 조회되었습니다. 변경 취소 후 최신 설정으로 다시 편집해 주세요."
        />
      )}
      <PayMethodWorkspaceStyle>
        <SettingsSection
          title="결제수단"
          action={
            <span className="settings-muted">
              {list.filter((item) => item.isExhibit).length} / {list.length} 노출
            </span>
          }
        >
          {list.map((method, index) => (
            <SortableSettingRow
              key={method.id}
              id={String(method.id)}
              group="payment-method"
              index={index}
              count={list.length}
              label={method.name}
              disabled={disabled}
              isSelected={selected?.id === method.id}
              onMove={(from, to) => draft.update((current) => moveItem(current, from, to))}
            >
              <div className="method-row">
                <button
                  className="method-select"
                  type="button"
                  aria-pressed={selected?.id === method.id}
                  onClick={() => setSelectedId(method.id)}
                >
                  <span className="method-name">
                    <span className="method-position">{index + 1}</span>
                    {method.name}
                  </span>
                  <span className="method-summary">
                    {method.eventName
                      ? '배지 · ' + method.eventName
                      : method.eventDetails.some((detail) => detail.content.trim())
                        ? '안내문 ' +
                          method.eventDetails.filter((detail) => detail.isActive && detail.content.trim()).length +
                          '개 노출'
                        : '혜택 문구 없음'}
                  </span>
                </button>
                <div className="method-status">
                  <Switch
                    checked={method.isExhibit}
                    disabled={disabled}
                    aria-label={method.name + ' 고객 노출'}
                    onChange={(checked) =>
                      draft.update((current) =>
                        current.map((item) => (item.id === method.id ? { ...item, isExhibit: checked } : item)),
                      )
                    }
                  />
                  <span>{method.isExhibit ? '노출' : '숨김'}</span>
                </div>
              </div>
            </SortableSettingRow>
          ))}
          <div className="method-help">손잡이를 끌거나 방향키로 순서를 변경할 수 있습니다.</div>
        </SettingsSection>
        {selected ? (
          <PayMethodBenefitEditor
            key={selected.id}
            payMethod={selected}
            disabled={disabled}
            onChange={(patch) =>
              draft.update((current) => current.map((item) => (item.id === selected.id ? { ...item, ...patch } : item)))
            }
          />
        ) : (
          <Empty description="등록된 결제수단이 없습니다." />
        )}
      </PayMethodWorkspaceStyle>
      {hasBlankLine && (
        <Alert className="settings-inline-alert" type="info" message="내용이 없는 안내문은 저장 시 제외됩니다." />
      )}
      <SettingsSaveBar
        isDirty={draft.isDirty}
        isSaving={mutation.isLoading}
        canUpdate={canUpdate}
        disabled={draft.hasRemoteChanges || changes.length === 0}
        onReset={draft.reset}
        onReview={handleReview}
      />
      <SettingsReviewModal
        open={isReviewOpen}
        title="결제수단·혜택 변경 검토"
        changes={changes}
        isSaving={mutation.isLoading}
        error={saveError}
        onSave={handleSave}
        onClose={() => setReviewOpen(false)}
      />
    </>
  );
};
